'use client';

import { abortableSleep } from '@fpo3/ts-commons-core/abortable-sleep';
import { useCallback, useRef, useState } from 'react';
import { type QueryCacheConfig, readCache, writeCache } from './query-cache-helpers';
import { useQueryRevalidation } from './use-query-revalidation';
import { useSyncRef } from './use-sync-ref';
export type { QueryCacheConfig } from './query-cache-helpers';
export interface UseApiQueryOptions<T> {
  enabled?: boolean;
  revalidateOnFocus?: boolean;
  transform?: (data: unknown) => T;
  cache?: QueryCacheConfig;
}
export interface UseApiQueryResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** True when revalidating with existing cached data visible. */
  refreshing: boolean;
  refetch: () => void;
}

/**
 * Lightweight data-fetching hook with stale-request tracking, optional
 * revalidation on focus/visibility, and caller-provided fetch function.
 *
 * When `cache` is set, uses the Cache API for stale-while-revalidate:
 * on mount, cached data is shown immediately while a background fetch runs.
 *
 * Pass `null` as endpoint to disable fetching (equivalent to enabled: false).
 */
export function useApiQuery<T>(fetchFn: (endpoint: string) => Promise<Response>, endpoint: string | null, options: UseApiQueryOptions<T> = {}): UseApiQueryResult<T> {
  const {
    enabled = true,
    revalidateOnFocus = false,
    transform,
    cache
  } = options;
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(enabled && endpoint != null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const fetchIdReference = useRef(0);
  const transformReference = useSyncRef(transform);
  const cacheReference = useSyncRef(cache);

  /** Executes the fetch sequence: cache read, optional delay, network request, and cache write. */
  const doFetch = useCallback(async () => {
    if (!enabled || endpoint == null) {
      fetchIdReference.current += 1;
      setLoading(false);
      return;
    }
    const fetchId = ++fetchIdReference.current;
    const cacheConfig = cacheReference.current;
    let hadCacheHit = false;

    // Phase 1: Read from cache
    if (cacheConfig) {
      const cached = await readCache(cacheConfig.cacheName, endpoint);
      if (fetchIdReference.current !== fetchId) return;
      if (cached !== undefined) {
        hadCacheHit = true;
        const transformed = transformReference.current ? transformReference.current(cached) : cached as T;
        setData(transformed);
        setLoading(false);
        setRefreshing(true);
      }
    }
    if (!hadCacheHit) {
      setLoading(true);
    }
    setError(null);

    // Phase 2: Delay if cache hit with revalidateDelay
    if (hadCacheHit && cacheConfig?.revalidateDelay) {
      await abortableSleep(cacheConfig.revalidateDelay, undefined);
      if (fetchIdReference.current !== fetchId) return;
    }

    // Phase 3: Network fetch
    try {
      const response = await fetchFn(endpoint);
      if (fetchIdReference.current !== fetchId) return;
      if (!response.ok) throw new Error(`Request failed: ${response.status}`);
      const json = await response.json();
      if (fetchIdReference.current !== fetchId) return;
      const transformed_0 = transformReference.current ? transformReference.current(json) : json;
      setData(transformed_0);

      // Write to cache
      if (cacheConfig) {
        void writeCache(cacheConfig.cacheName, endpoint, json);
      }
    } catch (error_) {
      if (fetchIdReference.current !== fetchId) return;
      const message = error_ instanceof Error ? error_.message : 'Unknown error';
      setError(message);
      // Keep cached data visible on error (SWR: don't clear data)
      if (!hadCacheHit) {
        setData(null);
      }
    } finally {
      if (fetchIdReference.current === fetchId) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [fetchFn, endpoint, enabled]);
  useQueryRevalidation(enabled, revalidateOnFocus, doFetch);
  return {
    data,
    loading,
    error,
    refreshing,
    refetch: doFetch
  };
}